// Plain-language names for what the audit log stores, so the history reads for someone who has
// never seen the database. Keys are the backend's CLR names; anything missing falls back to a
// humanised version of the technical name. Entity names live in entityLabels.ts, column names in
// columnLabels.ts.
export { ENTITY_LABELS } from "./entityLabels";
export { COLUMN_LABELS } from "./columnLabels";
export { LINK_COLUMNS } from "./columnLabelsCatalogue";

// Where one entity gives a shared column a different meaning.
export const ENTITY_COLUMN_LABELS: Record<string, Record<string, string>> = {
  Schedule: { Duration: "Minuta nga nisja", Description: "Shënimi i ndalesës" },
  Trip: { Duration: "Kohëzgjatja e udhëtimit (minuta)" },
  OperatorEntityCommisionFee: { EntityId: "Orari" }
};

// Technical columns nobody outside engineering needs to see.
export const HIDDEN_COLUMNS = new Set([
  "OldId",
  "OldPathId",
  "OldLineId",
  "OldTicketId",
  "CreatedBy",
  "UpdatedBy",
  "UpdateDate",
  // "Last touched" dates some mapping tables carry; they move on every save and say nothing.
  "DateUpdated",
  "UpdatedDate",
  // An invoice correction's copy of the journey key, kept for reconciliation, not for reading.
  "UniqueJourneyIdSnapshot",
  // Keys and external ids: they identify a row to another system, never to a person.
  "JourneyGuid",
  "UniqueJourneyId",
  "UniqueReservationGuid",
  "JourneyReservationTripMappingGuid",
  "InvitationGuid",
  "PictureGuid",
  "UserGuid",
  "HangfireJobId",
  "OcsTemplateId",
  "OcsPackageId",
  "SSOUserId",
  "EsimProfileId",
  "GifterEsimUserId",
  "LastProcessedDeviceId",
  // "Already sent / already synced" bookkeeping: it moves on its own and says nothing about the edit.
  "ReceiptsChecked",
  "EmailSent",
  "EmailSentAtUtc",
  "ClaimExpirationWarningSent",
  "Expiry7DayNotifiedForUtc",
  "Expiry24HourNotifiedForUtc",
  "Expiry30DayNotifiedForUtc",
  "Expiry90DayNotifiedForUtc",
  "LastSyncedUtc",
  "DocSheetUpdatedAtUtc",
  "LastTransactionAtUtc",
  // A refund's own bookkeeping: which job has picked it up, how often it retried, who was told.
  "Processed",
  "RefundSwept",
  "RefundSweepAttempts",
  "AdminsNotified"
]);

export { ENUM_VALUE_LABELS } from "./enumLabels";

// Which column on a child entity points at its parent, to name the parent in the details.
export const PARENT_COLUMN: Record<string, string> = {
  Schedule: "TripId",
  Price: "TripId",
  CustomTripWorkingDay: "TripId",
  CustomTripReservabilityDate: "TripId",
  BusTripMapping: "TripId",
  Trip: "LineId",
  Line: "OperatorId",
  OperatorCommisionFee: "OperatorId",
  OperatorEntityCommisionFee: "EntityId",
  TravelBundleDetail: "TravelBundleId",
  TravelBundleIncludeMapping: "TravelBundleId",
  TravelBundleLocationMapping: "TravelBundleId",
  TravelBundlePictureMapping: "TravelBundleId",
  BlogPictureMapping: "BlogId",
  OperatorInvoiceAdjustment: "TicketId",
  TripsNotices: "TripId",
  CustomReservationThreshold: "OperatorId",
  // Rows that name their own owner: the id column here, the type column in PARENT_TYPE_COLUMN.
  Refund: "EntityId",
  LocalizedProperty: "EntityId",
  EntityPictureMapping: "EntityId",
  EntityPageSection: "EntityId",
  CreditReservationMapping: "EntityId",
  WalletCredit: "EntityId",
  WalletTransaction: "EntityId",
  FAQAnswer: "EntityId",
  TicketUserInfo: "TicketId"
};

// For rows whose owner is written into them: the column holding the owner's type. The parent line
// already says both, so neither column is worth repeating among the changed fields.
export const PARENT_TYPE_COLUMN: Record<string, string> = {
  Refund: "EntityName",
  LocalizedProperty: "LocaleKeyGroup",
  EntityPictureMapping: "EntityName",
  EntityPageSection: "EntityName",
  CreditReservationMapping: "EntityName",
  WalletCredit: "EntityName",
  WalletTransaction: "EntityName",
  FAQAnswer: "EntityName"
};

// Short forms for a narrow cell, where the full names crowd out the value that changed.
export const DAY_NAMES_SHORT: Record<number, string> = {
  1: "Hën",
  2: "Mar",
  3: "Mër",
  4: "Enj",
  5: "Pre",
  6: "Sht",
  7: "Die"
};

export const DAY_NAMES: Record<number, string> = {
  1: "E hënë",
  2: "E martë",
  3: "E mërkurë",
  4: "E enjte",
  5: "E premte",
  6: "E shtunë",
  7: "E diel"
};
